import { useEffect, useState, useCallback } from "react";
import { NavLink, useLocation, useNavigate, Link } from "react-router-dom";
import {
  Calendar, CalendarDays, Users, Wrench, Receipt, Menu, LogOut,
  Settings as SettingsIcon, FileText, UserCog, ShieldAlert, BarChart3,
  Wallet, CreditCard, Rocket, UsersRound, Mail, FileSpreadsheet, GitCompareArrows, Package,
  ShieldCheck, Banknote,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import OnboardingBanner from "@/components/OnboardingBanner";
import AlertsBadge from "@/components/AlertsBadge";
import ApprovalsBadge from "@/components/ApprovalsBadge";
import SubscriptionBanner from "@/components/SubscriptionBanner";
import LegalReacceptModal from "@/components/LegalReacceptModal";
import BillingAddressRequiredModal from "@/components/BillingAddressRequiredModal";
import { GlobalSearchDesktop, GlobalSearchMobile } from "@/components/GlobalSearch";
import { toast } from "sonner";

// Phase 63.2 — desktop top-nav unchanged (5 entries). Bottom nav is collapsed
// to 3 keystroke-friendly destinations driven by the calendar's `?view` param
// + the Jobs list.
const allNav = [
  { to: "/calendar", label: "Calendar", icon: Calendar, testId: "nav-calendar", plans: ["trial", "basic", "pro", "custom"] },
  { to: "/customers", label: "Customers", icon: Users, testId: "nav-customers", plans: ["trial", "basic", "pro", "custom"] },
  { to: "/jobs", label: "Jobs", icon: Wrench, testId: "nav-jobs", plans: ["trial", "basic", "pro", "custom"] },
  { to: "/products", label: "Products", icon: Package, testId: "nav-products", plans: ["trial", "basic", "pro", "custom"] },
  { to: "/accounts", label: "Accounts", icon: Receipt, testId: "nav-accounts", plans: ["trial", "pro", "custom"] },
  { to: "/finance", label: "Finance", icon: Banknote, testId: "nav-finance", plans: ["trial", "pro", "custom"] },
  { to: "/my-cash", label: "My Cash", icon: Wallet, testId: "nav-my-cash", plans: ["trial", "basic", "pro", "custom"] },
  { to: "/reports", label: "Reports", icon: BarChart3, testId: "nav-reports", plans: ["trial", "pro", "custom"] },
];

// Phase 63.2 — bottom nav definitions. Calendar 7/30 deep-link the same page
// via ?view; Jobs stays at /jobs. Active highlighting reads the search-params
// so the two Calendar buttons don't both light up at once.
const bottomNav = [
  { key: "cal-week", to: "/calendar?view=week", label: "Calendar 7", icon: Calendar, testId: "bottom-nav-calendar-week" },
  { key: "cal-month", to: "/calendar?view=month", label: "Calendar 30", icon: CalendarDays, testId: "bottom-nav-calendar-month" },
  { key: "jobs", to: "/jobs", label: "Jobs", icon: Wrench, testId: "bottom-nav-jobs" },
];

const planBadgeStyles = {
  trial: "bg-sky-100 text-sky-800 border-sky-300",
  basic: "bg-slate-100 text-slate-800 border-slate-300",
  pro: "bg-accent/10 text-accent border-accent/40",
  custom: "bg-emerald-100 text-emerald-800 border-emerald-300",
};
const planLabels = { trial: "Trial", basic: "Basic", pro: "Pro", custom: "Custom" };

export default function Layout({ children }) {
  const { manager, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [auditMode, setAuditMode] = useState(false);
  const [planCode, setPlanCode] = useState("custom");
  const [trialDaysRem, setTrialDaysRem] = useState(null);

  const refreshAudit = useCallback(() => {
    api.get("/settings")
      .then(({ data }) => setAuditMode(!!data?.audit_mode))
      .catch(() => {});
  }, []);

  const refreshPlan = useCallback(() => {
    api.get("/subscription/status")
      .then(({ data }) => {
        setPlanCode(data?.plan_code || "custom");
        setTrialDaysRem(data?.trial_days_remaining);
      })
      .catch(() => {});
  }, []);

  useEffect(() => { refreshAudit(); refreshPlan(); }, [refreshAudit, refreshPlan, location.pathname]);
  useEffect(() => {
    const handler = () => { refreshAudit(); refreshPlan(); };
    window.addEventListener("jobsbook:settings-changed", handler);
    return () => window.removeEventListener("jobsbook:settings-changed", handler);
  }, [refreshAudit, refreshPlan]);

  const disableAudit = async () => {
    try {
      await api.post("/settings/audit-mode", { enabled: false });
      setAuditMode(false);
      toast.success("Audit mode disabled");
      window.dispatchEvent(new Event("jobsbook:settings-changed"));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const visibleNav = allNav.filter((n) => n.plans.includes(planCode));
  const showUpgrade = planCode === "trial" || planCode === "basic";

  return (
    <div className="min-h-screen flex flex-col">
      <div className="seam" data-testid="industrial-seam" />

      <SubscriptionBanner />

      {auditMode && (
        <div
          className="bg-amber-400 text-amber-950 border-b-2 border-amber-600"
          data-testid="cash-filter-banner"
        >
          <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-2.5 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 mono text-xs uppercase tracking-[0.2em] font-bold">
              <ShieldAlert className="h-4 w-4" />
              Cash-only jobs hidden from lists. Disable in Settings.
            </div>
            {manager?.role === "owner" && (
              <Button
                size="sm"
                variant="outline"
                onClick={disableAudit}
                className="bg-white/50 hover:bg-white border-amber-700 text-amber-950 h-7"
                data-testid="cash-filter-banner-disable"
              >
                Show all jobs
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border" data-testid="top-bar">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
            <div className="h-9 w-9 grid place-items-center bg-primary text-primary-foreground rounded-[6px]">
              <Wrench className="h-4 w-4" />
            </div>
            <div className="leading-tight">
              <div className="text-base md:text-lg font-extrabold tracking-tight">JobsBook</div>
              <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground hidden sm:block">
                Bookings & invoicing
              </div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden sm:flex items-center gap-1" data-testid="desktop-nav">
            {visibleNav.map(({ to, label, testId }) => {
              const active = location.pathname.startsWith(to);
              return (
                <NavLink
                  key={to}
                  to={to}
                  className={`px-3 py-2 text-sm font-semibold rounded-md transition-colors ${
                    active ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-secondary"
                  }`}
                  data-testid={testId + "-desktop"}
                >
                  {label}
                </NavLink>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            {/* Phase P.8 — global manager search. Desktop = persistent
                bar (hidden below sm to save space); mobile = icon that
                opens a full-screen modal. */}
            <div className="hidden sm:block">
              <GlobalSearchDesktop />
            </div>
            <GlobalSearchMobile />

            {/* Plan badge */}
            <Link to="/billing" className={`hidden md:inline-flex items-center gap-1.5 text-[10px] mono uppercase tracking-[0.18em] font-bold border rounded-md px-2 py-1 ${planBadgeStyles[planCode] || planBadgeStyles.custom}`}
                  data-testid="plan-badge">
              {planLabels[planCode] || "Custom"}
              {planCode === "trial" && trialDaysRem !== null && trialDaysRem !== undefined && (
                <span className="opacity-80">· {Math.max(0, trialDaysRem)}d</span>
              )}
            </Link>
            {/* Phase 17: role badge */}
            {manager?.role && (
              <span className={`hidden md:inline-flex items-center text-[10px] mono uppercase tracking-[0.18em] font-bold border rounded-md px-2 py-1 ${
                manager.role === "owner" ? "bg-accent/10 text-accent border-accent/40" : "bg-slate-100 text-slate-700 border-slate-300"
              }`} data-testid="role-badge">
                {manager.role === "owner" ? "Owner" : "Admin"}
              </span>
            )}
            {showUpgrade && manager?.role === "owner" && (
              <Link to="/billing" className="hidden md:inline-flex items-center gap-1 text-[10px] mono uppercase tracking-[0.18em] font-bold text-accent hover:text-accent/80"
                    data-testid="plan-upgrade-link">
                <Rocket className="h-3 w-3" /> Upgrade
              </Link>
            )}

            <ApprovalsBadge />
            <AlertsBadge />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="user-menu-trigger" className="gap-2">
                  <Menu className="h-4 w-4" />
                  <span className="hidden sm:inline mono text-xs uppercase">{manager?.name || "Menu"}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72 max-h-[85vh] overflow-y-auto shadow-2xl" data-testid="user-menu-content">
                <DropdownMenuLabel className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  {manager?.email}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />

                {/* Phase 63.2 — Group 1: Operations */}
                <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground/80 font-bold py-1" data-testid="menu-group-operations">
                  Operations
                </DropdownMenuLabel>
                <DropdownMenuItem onClick={() => navigate("/customers")} data-testid="menu-customers">
                  <Users className="h-4 w-4 mr-2" /> Customers
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/customers/duplicates")} data-testid="menu-customer-duplicates">
                  <GitCompareArrows className="h-4 w-4 mr-2" /> Customer Duplicates
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/staff")} data-testid="menu-staff">
                  <UserCog className="h-4 w-4 mr-2" /> Staff
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/templates")} data-testid="menu-templates">
                  <FileText className="h-4 w-4 mr-2" /> Job Templates
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/invoices/sent")} data-testid="menu-sent-emails">
                  <Mail className="h-4 w-4 mr-2" /> Sent emails
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                {/* Phase 63.2 — Group 2: Money */}
                <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground/80 font-bold py-1" data-testid="menu-group-money">
                  Money
                </DropdownMenuLabel>
                <DropdownMenuItem onClick={() => navigate("/accounts")} data-testid="menu-accounts">
                  <Receipt className="h-4 w-4 mr-2" /> Accounts
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/reports")} data-testid="menu-reports">
                  <BarChart3 className="h-4 w-4 mr-2" /> Reports
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/ledger")} data-testid="menu-ledger">
                  <Wallet className="h-4 w-4 mr-2" /> Ledger
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/billing")} data-testid="menu-billing">
                  <CreditCard className="h-4 w-4 mr-2" /> Billing
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                {/* Phase 63.2 — Group 3: Admin */}
                <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground/80 font-bold py-1" data-testid="menu-group-admin">
                  Admin
                </DropdownMenuLabel>
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                  <SettingsIcon className="h-4 w-4 mr-2" /> Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/team")} data-testid="menu-team">
                  <UsersRound className="h-4 w-4 mr-2" /> Team
                </DropdownMenuItem>
                {typeof window !== "undefined" && localStorage.getItem("jb_platform_token") && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-super-admin">
                    <ShieldCheck className="h-4 w-4 mr-2" /> Super-admin console
                  </DropdownMenuItem>
                )}

                <DropdownMenuSeparator />

                {/* Phase 63.2 — Group 4: Account */}
                <DropdownMenuItem
                  onClick={async () => {
                    await logout();
                    navigate("/login");
                  }}
                  data-testid="menu-logout"
                  className="text-destructive focus:text-destructive"
                >
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Phase 64 — Onboarding reminder banner. Self-hides when the tenant
          is fully set up or after the manager dismisses it. */}
      <OnboardingBanner />

      {/* Phase P.18 — one-shot legal reacceptance modal for existing users
          whose recorded terms_version pre-dates the current LEGAL_VERSION. */}
      <LegalReacceptModal />

      {/* Phase P.26 — one-shot billing-address modal for legacy tenants
          that predate the mandatory-address signup requirement. */}
      <BillingAddressRequiredModal />

      <main className="flex-1 pb-20 sm:pb-0">{children}</main>

      {/* Bottom nav (mobile, ≤640px) — Phase 63.2: 3-button layout
          1. Calendar 7 (week view)
          2. Calendar 30 (month view)
          3. Jobs */}
      <nav
        className="sm:hidden fixed bottom-0 inset-x-0 z-30 bg-background border-t border-border"
        data-testid="bottom-nav"
      >
        <div className="grid grid-cols-3">
          {bottomNav.map(({ key, to, label, icon: Icon, testId }) => {
            // `to` may include a query string — split it off for the match.
            const [path, query] = to.split("?");
            const params = new URLSearchParams(query || "");
            const wantView = params.get("view");
            const isCalendarRoute = location.pathname.startsWith("/calendar");
            const currentView = new URLSearchParams(location.search).get("view") || "week";
            const active = path === "/jobs"
              ? location.pathname.startsWith("/jobs")
              : (isCalendarRoute && (wantView ? wantView === currentView : true));
            return (
              <NavLink
                key={key}
                to={to}
                className={`flex flex-col items-center justify-center min-h-[56px] py-2 gap-0.5 transition-colors ${
                  active ? "text-accent" : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid={testId}
              >
                <Icon className="h-5 w-5" />
                <span className="text-[11px] font-semibold">{label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
